import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
import BackToList from '../components/BackToList';
import withApi from '../../components/higherOrderComponents/withApi';
import Guid from '../components/Guid';
import RelatedLinks from '../components/RelatedLinks';
import StatusPill from '../components/StatusPill';

/**
 * Payment method detail, for every kind — not only the two the old admin has
 * pages for.
 *
 * `/paymentmethod/creditcard/:id` and `/paymentmethod/undefined/:id` are two
 * files there and one here, because they are the same nine fields plus four the
 * card has — cardholder, masked number, expiry, scheme. "Undefined" is the type's
 * name in the enum, not a missing value, which is worth knowing before you go
 * looking for the bug.
 *
 * **Production has five types**: CreditCard, UserCredit, MobilePay, Frisbii and
 * Undefined. The route covered two, so a MobilePay or Frisbii row — a third of
 * the list — landed on Home when clicked. The route takes the kind as a
 * parameter now, and this page decides what to show from the record rather than
 * from which of two routes reached it. The card fields appear when there is card
 * data, which is the honest test: both api functions post to the same endpoint,
 * so the kind never changed the answer.
 *
 * No actions: the old pages have none either. A card is changed by the user in
 * the app, not from here.
 */

const fmtDate = value => (value ? String(value).replace('T', ' ').slice(0, 19) : '—');

// The card number never arrives whole — the payload carries the last digits and
// the scheme, which is what "•••• 4242" is made of.
const maskedNumber = method => {
  const last = method.LastFourDigits ?? method.Last4 ?? method.MaskedNumber ?? method.Number;
  if (!last) return null;
  const digits = String(last).replace(/\D/g, '').slice(-4);
  return digits ? `•••• ${digits}` : String(last);
};

const PaymentMethodInternal = ({ api }) => {
  const { paymentMethodId, kind } = useParams();

  const [method, setMethod] = useState(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      // Both functions post to `/admin/paymentmethods/{id}`, so which one is
      // called cannot change the answer.
      const data = await api.paymentMethods.getCreditCard(paymentMethodId);

      if (!data || data.success === false || !(data.Id || data.id)) {
        setFailed(true);
        return;
      }
      setMethod(data);
      setFailed(false);
    } catch (error) {
      setFailed(true);
    }
  }, [api, paymentMethodId]);

  useEffect(() => {
    load();
  }, [load]);

  if (failed) {
    return (
      <div className="next-page">
        <BackToList to="/next/payment-methods">← All payment methods</BackToList>
        <div className="next-note next-note--error">
          Could not load this payment method.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  if (!method) return <div className="next-page next-muted">Loading…</div>;

  // What the record is, not which URL reached it: the four card fields belong on
  // anything carrying card data and on nothing that is not.
  const isCard = !!(maskedNumber(method) || method.CardType || method.ExpiryMonth)
    || String(method.Type || kind || '').toLowerCase() === 'creditcard';

  const expiry = method.Expiration
    ? `${String(method.Expiration.Month ?? '').padStart(2, '0')}/${method.Expiration.Year ?? ''}`
    : null;

  const facts = [
    ['Status', method.Status
      ? <StatusPill status={method.Status}>{method.Status}</StatusPill>
      : <span className="next-muted">—</span>],
    ['Type', method.Type || <span className="next-muted">—</span>],
    ['Display name', method.DisplayName || <span className="next-muted">—</span>],
    ['Is default', <span className={`next-pill next-pill--${method.IsDefault ? 'ok' : 'neutral'}`}>
      {method.IsDefault ? 'Yes' : 'No'}
    </span>],
    ['User id', method.UserId
      ? <Guid value={method.UserId} to={`/next/user/${method.UserId}`} />
      : <span className="next-muted">—</span>],
    ['Token', method.Token ? <span className="next-mono-list">{method.Token}</span> : <span className="next-muted">—</span>],
    ['Created at (CET)', fmtDate(method.CreatedAtCet)],
    ...(isCard ? [
      ['Cardholder', method.CardholderName || <span className="next-muted">—</span>],
      ['Card number', maskedNumber(method) || <span className="next-muted">—</span>],
      ['Expires', expiry || <span className="next-muted">—</span>],
      ['Card type', method.CardType || <span className="next-muted">—</span>]
    ] : [])
  ];

  const title = method.DisplayName
    || (isCard ? (maskedNumber(method) || 'Credit card') : 'Payment method');

  return (
    <div className="next-page next-page--wide">
      <BackToList to="/next/payment-methods">← All payment methods</BackToList>

      <header className="next-detail__head">
        <div className="next-detail__title">
          <h1>{title}</h1>
          {method.Status && <StatusPill status={method.Status}>{method.Status}</StatusPill>}
        </div>
        <span className="next-detail__id">{method.Id || paymentMethodId}</span>
      </header>

      <div className="next-detail">
        <div className="next-detail__main">
          <section className="next-card">
            <div className="next-card__head">
              <h2>{isCard ? 'Card' : 'Payment method'}</h2>
            </div>
            <div className="next-facts">
              {facts.map(([label, value]) => (
                <div className="next-fact" key={label}>
                  <span className="next-fact__label">{label}</span>
                  <span className="next-fact__value">{value}</span>
                </div>
              ))}
            </div>
          </section>
        </div>

        <aside className="next-detail__side">
          <section className="next-card">
            <div className="next-card__head"><h2>Links</h2></div>
            <RelatedLinks
              items={[
                method.UserId && {
                  label: 'User',
                  hint: 'who owns it',
                  to: `/next/user/${method.UserId}`
                },
                {
                  label: 'Transactions',
                  hint: 'paid with this method',
                  to: `/next/transactions?paymentMethodId=${method.Id || paymentMethodId}`
                },
                method.UserId && {
                  label: 'Other payment methods',
                  hint: 'of this user',
                  to: `/next/payment-methods?userId=${method.UserId}`
                },
                method.UserId && {
                  label: 'Purchases',
                  hint: 'by this user',
                  to: `/next/purchases?userId=${method.UserId}`
                }
              ]}
            />
          </section>

          {/*
            No Actions panel: the old pages have none. A card is added or removed
            by the user in the app, and nothing here can do it for them.
          */}
        </aside>
      </div>
    </div>
  );
};

PaymentMethodInternal.propTypes = {
  // The kind comes from the URL now rather than from a prop.
  api: PropTypes.object.isRequired
};

const PaymentMethod = withApi(PaymentMethodInternal);

PaymentMethod.requiresLogin = true;

export default PaymentMethod;
